import { hasIn, isObjectLike } from "lodash";
import { DEFAULT_VIDEO_CAPTURE_FPS, DEFAULT_VIDEO_CAPTURE_HEIGHT, DEFAULT_VIDEO_CAPTURE_WIDTH, HIGH_QUALITY_LAYER_VIDEO_BITRATE, LOW_QUALITY_LAYER_VIDEO_BITRATE, LOW_QUALITY_LAYER_VIDEO_FRAMERATE, MID_QUALITY_LAYER_VIDEO_BITRATE, } from "gather-video-common/dist/src/public/settings";
import { EXECUTION_QUEUE_DESTROYED_ERROR_MESSAGE } from "gather-video-common/dist/src/public/ExecutionQueue";
import { Logger } from "./Logger";
import { ErrorContext } from "gather-common/dist/src/public/utils";
export const MEDIA_DEVICE_AUDIO_INPUT = "audioinput";
export const MEDIA_DEVICE_AUDIO_OUTPUT = "audiooutput";
export const MEDIA_DEVICE_VIDEO_INPUT = "videoinput";
export const DEFAULT_AUDIO_DEVICE_ID = "default";
export function summarizeMediaStreamTrack(track) {
    if (!track)
        return {};
    return {
        id: track.id,
        label: track.label,
        enabled: track.enabled,
        hasOutput: !track.muted,
        readyState: track.readyState,
        contentHint: track.contentHint,
    };
}
export var ProduceState;
(function (ProduceState) {
    ProduceState[ProduceState["Ended"] = 0] = "Ended";
    ProduceState[ProduceState["Loading"] = 1] = "Loading";
    ProduceState[ProduceState["Connected"] = 2] = "Connected";
    ProduceState[ProduceState["Errored"] = 3] = "Errored";
})(ProduceState || (ProduceState = {}));
export function ConsumerKindToMediaKind(kind) {
    if (kind === "audio" || kind === "sound") {
        return "audio";
    }
    else if (kind === "video" || kind === "screen") {
        return "video";
    }
    else {
        throw new Error(`Unexpected ConsumerKind ${kind}`);
    }
}
export const calculateBitrate = (bytes = 0, prevBytes = 0, timeDelta = 0) => {
    if (bytes <= 0 || prevBytes > bytes || timeDelta <= 0)
        return 0;
    return ((bytes - Math.max(0, prevBytes)) * 8) / Math.max(1, timeDelta);
};
export const isNode = (nodeName, target) => target.nodeName === nodeName;
export const isMediaStream = (target) => isObjectLike(target) && hasIn(target, "getAudioTracks") && hasIn(target, "getVideoTracks");
export function ignoreExecutionQueueExceptions(error) {
    if (error.message === EXECUTION_QUEUE_DESTROYED_ERROR_MESSAGE) {
        Logger.warn(EXECUTION_QUEUE_DESTROYED_ERROR_MESSAGE);
        return;
    }
    throw error;
}
export const shouldUseHighQualityScreenshare = (track) => track.contentHint === "detail" || track.contentHint === "motion";
const getFps = (spatialLayer) => spatialLayer === 0 ? LOW_QUALITY_LAYER_VIDEO_FRAMERATE : DEFAULT_VIDEO_CAPTURE_FPS;
export function getPixelRateByLayer(spatialLayer, temporalLayer) {
    const fps = temporalLayer !== 0 ? getFps(spatialLayer) : getFps(spatialLayer) / 2;
    switch (spatialLayer) {
        case 0:
            return (DEFAULT_VIDEO_CAPTURE_WIDTH / 4) * (DEFAULT_VIDEO_CAPTURE_HEIGHT / 4) * fps;
        case 1:
            return (DEFAULT_VIDEO_CAPTURE_WIDTH / 2) * (DEFAULT_VIDEO_CAPTURE_HEIGHT / 2) * fps;
        case 2:
            return DEFAULT_VIDEO_CAPTURE_WIDTH * DEFAULT_VIDEO_CAPTURE_HEIGHT * fps;
        default:
            return 0;
    }
}
export function getBitRateByLayer(spatialLayer, temporalLayer) {
    const factor = temporalLayer !== 0 ? 1 : 0.6;
    switch (spatialLayer) {
        case 0:
            return LOW_QUALITY_LAYER_VIDEO_BITRATE * factor;
        case 1:
            return MID_QUALITY_LAYER_VIDEO_BITRATE * factor;
        case 2:
            return HIGH_QUALITY_LAYER_VIDEO_BITRATE * factor;
        default:
            return 0;
    }
}
export function getSpatialTemporalLayerByVideoStat(width, fps) {
    const spatialLayer = width <= DEFAULT_VIDEO_CAPTURE_WIDTH / 4 ? 0 : width <= DEFAULT_VIDEO_CAPTURE_WIDTH / 2 ? 1 : 2;
    const temporalLayer = fps <= getFps(spatialLayer) / 2 ? 0 : 1;
    if (spatialLayer === 0 && temporalLayer === 0 && fps < getFps(spatialLayer) / 4) {
        return { spatialLayer: 0, temporalLayer: -1 };
    }
    return { spatialLayer, temporalLayer };
}
export function nextLayerUp(spatialLayer, temporalLayer) {
    if (spatialLayer >= 2 && temporalLayer >= 1) {
        return {
            spatialLayer: 2,
            temporalLayer: 1,
        };
    }
    if (temporalLayer <= 0) {
        return {
            spatialLayer,
            temporalLayer: temporalLayer + 1,
        };
    }
    else {
        return {
            spatialLayer: spatialLayer + 1,
            temporalLayer: 0,
        };
    }
}
export function nextLayerDown(spatialLayer, temporalLayer) {
    if (spatialLayer <= 0 && temporalLayer <= 0) {
        return {
            spatialLayer: undefined,
            temporalLayer: undefined,
        };
    }
    if (temporalLayer === 1) {
        return {
            spatialLayer,
            temporalLayer: 0,
        };
    }
    else {
        return {
            spatialLayer: spatialLayer - 1,
            temporalLayer: 1,
        };
    }
}
export function compareLayers(newSpatialLayer, newTemporalLayer, spatialLayer, temporalLayer) {
    if (newSpatialLayer === spatialLayer && newTemporalLayer === temporalLayer)
        return 0;
    if (newSpatialLayer < spatialLayer)
        return -1;
    if (newSpatialLayer > spatialLayer)
        return 1;
    return newTemporalLayer < temporalLayer ? -1 : 1;
}
export const prefixKeys = (prefix, data) => Object.fromEntries(Object.entries(data).map(([key, value]) => [`${prefix}.${key}`, value]));
export const checkVideoTrackHasOutputAfterFiveSec = (consumer, map) => {
    const existingTimeout = map.get(consumer.id);
    if (existingTimeout) {
        clearTimeout(existingTimeout);
        map.delete(consumer.id);
    }
    map.set(consumer.id, setTimeout(() => {
        map.delete(consumer.id);
        if (consumer.closed || consumer.paused)
            return;
        const track = consumer.track;
        if (track.kind === "video" && track.enabled && track.readyState === "live") {
            if (track.muted) {
                Logger.error(`Video track has no output after 5 seconds`, new ErrorContext({ consumer: consumer.id, track: track.id }));
                return;
            }
            checkVideoTrackHasOutputAfterFiveSec(consumer, map);
        }
    }, 5000));
};
//# sourceMappingURL=utils.js.map